import type { LearnTopic } from '../types';
import { $, width } from '../format';

export const buyingVsSellingTopic: LearnTopic = {
  id: 'buying-vs-selling',
  tier: 'basics',
  title: 'Buying versus selling',
  summary:
    'Buying caps your loss at the premium; selling caps your gain at it. That trade-off is why a bought leg can insure a sold one.',
  body: [
    'Buying an option caps your loss at the premium and leaves the gain open. Selling one caps your gain at the premium and leaves the loss open. That is the whole choice, and every position is some arrangement of those two halves.',
    'Because the halves are mirror images, a bought option can insure a sold one. Insurance is the right word. A long put on shares you own behaves like fire insurance: you pay the premium, and if nothing burns you never see it again — but if the house burns, the payout is there and you knew its size in advance.',
    'Be careful with the opposite case. A covered call — selling a call against shares you already own — protects nothing if the stock falls. It caps what you can make above the strike and leaves the whole way down open. It is a fine trade; it is not a hedge.',
    'What makes a sold obligation real is collateral, and the broker takes it whether you think of it that way or not. Shares sit behind a covered call. Cash sits frozen behind a cash-secured put. The distance between the two strikes sits behind a sold spread. A short with nothing behind it is called uncovered, or naked, and it is the one shape with no ceiling on what it can lose — which is why brokers hold so much against it.',
    "In an iron condor the bought legs are the insurance, and they are what bounds the collateral: the width between the two strikes becomes the requirement, in place of the open-ended one a bare short would carry. Sell a put and a call with nothing bought outside them and you have a strangle, open-ended on both sides. Buy a wing — the option you buy further out, beyond the one you sold — and the loss on that side stops at that width, less the credit. You pay for the ceiling out of the credit: your shorts sit at 10 to 15 delta (delta reads roughly as the chance of finishing in the money) and your wings a few strikes further out, which gives up some of the premium to buy a known worst case. The buyers-and-sellers examples and the lessons behind them are Howard's.",
  ],
  explain(ctx) {
    const { shortPut, longPut, shortCall, longCall, credit } = ctx;
    const sentences: string[] = [];

    const sold = [
      shortPut != null ? `the ${shortPut} put` : null,
      shortCall != null ? `the ${shortCall} call` : null,
    ].filter((leg): leg is string => leg != null);

    if (sold.length === 0) {
      sentences.push('You have sold nothing on this position, so nothing here was paid to you for taking on an obligation.');
    } else if (credit != null) {
      sentences.push(`The legs you sold — ${sold.join(' and ')} — are where the ${$(credit)} a share credit came from.`);
    } else {
      sentences.push(
        `The legs you sold — ${sold.join(' and ')} — are where a credit would have come from, but no credit is recorded for this position.`,
      );
    }

    const side = (name: 'put' | 'call', short: number | null, long: number | null) => {
      if (short == null) return;
      if (long == null) {
        sentences.push(
          `Nothing is bought beyond your short ${short} ${name}, which makes that ${name} side uncovered: there is no ceiling on what it can lose.`,
        );
        return;
      }
      const spreadWidth = width(short, long);
      sentences.push(
        credit != null
          ? `The ${long} ${name} you bought caps the ${name} side: the most it can lose is ${$(spreadWidth - credit)} a share, the ${spreadWidth} width less the credit.`
          : `The ${long} ${name} you bought caps the ${name} side at the ${spreadWidth} width, less whatever credit was collected.`,
      );
    };

    side('put', shortPut, longPut);
    side('call', shortCall, longCall);

    return sentences;
  },
  appliesTo() {
    return true;
  },
};
